import { Button } from '@/components/ui/button';
import { ToolPageFragment } from '@/gql/graphql';
import { DynamicLink } from '@/reactRouterPlugins';
import { ArticleBanner } from '@/routes/resource/key/components/articleBanner';
import { fragmentManager } from '@/services/fragmentManager';
import { FormattedMessage } from 'react-intl';
import { useLoaderData, useLocation } from 'react-router-dom';
import { ArticleAuxiliary } from '../components/articleAuxiliary';
import { ArticleBody } from '../components/articleBody';
import { ArticleFooterWrapper } from '../components/articleFooterWrapper';
import { ArticleIntro } from '../components/articleIntro';
import { ArticlePreTitle, PreTitleDate } from '../components/articlePreTitle';
import { ArticleSkeleton } from '../components/articleSkeleton';
import { ArticleTextContainer } from '../components/articleTextContainer';
import { ArticleTitle } from '../components/articleTitle';
import { PageContainer } from '../components/pageContainer';
import { SecondaryLinks } from '../components/secondaryLinks';
import {
  createResourceLoaderWithRedirect,
  ResourceLoaderResult,
} from '../createResourceLoaderWithRedirect';
import { useNotifyOfPartialDataIfErrors } from '@/routes/rootErrorPage';
import PageMetaData from '@/components/PageMetaData';

export const ToolPageSkeleton = ArticleSkeleton;

fragmentManager.register(/* GraphQL */ `
  fragment ToolPage on Tool {
    id
    title
    summary
    excerpt
    body
    primaryImage {
      ...ArticleBanner
    }
    primaryLink {
      label
      url
    }
    secondaryLinks {
      label
      url
    }
    citation
    createdAt
    author
    rights
    rightsHolder
    publicationDate
  }
`);

export const toolPageLoader = createResourceLoaderWithRedirect({
  fragment: 'ToolPage',
  resourceType: 'Tool',
});

export function ToolPage() {
  const { data, errors } = useLoaderData() as ResourceLoaderResult<ToolPageFragment>;
  useNotifyOfPartialDataIfErrors(errors);
  const { resource } = data;
  const location = useLocation();

  return (
    <article>
      <PageMetaData
        title={resource.title}
        description={resource.excerpt}
        path={location.pathname}
        imageUrl={resource.primaryImage?.file.normal}
        imageAlt={resource.primaryImage?.description}
      />

      <PageContainer topPadded bottomPadded className="g-bg-white">
        <ArticleTextContainer className="g-mb-10">
          <ArticlePreTitle
            clickable
            secondary={<PreTitleDate date={resource.publicationDate ?? resource.createdAt} />}
          >
            <DynamicLink to="/resource/search?contentType=tool">
              <FormattedMessage id="cms.contentType.tool" />
            </DynamicLink>
          </ArticlePreTitle>

          <ArticleTitle dir="auto" dangerouslySetTitle={{ __html: resource.title }} />

          {resource.summary && (
            <ArticleIntro dangerouslySetIntro={{ __html: resource.summary }} className="g-mt-2" />
          )}

          {resource.primaryLink && (
            <div className="g-mt-4" dir="auto">
              <Button asChild>
                <DynamicLink to={resource.primaryLink.url}>
                  {resource.primaryLink.label}
                </DynamicLink>
              </Button>
            </div>
          )}
        </ArticleTextContainer>

        <ArticleBanner className="g-mt-8 g-mb-6" image={resource?.primaryImage} />

        <ArticleTextContainer>
          {resource.body && (
            <ArticleBody dangerouslySetBody={{ __html: resource.body }} className="g-mt-2" />
          )}

          <ArticleFooterWrapper>
            {resource.secondaryLinks && (
              <ArticleAuxiliary>
                <SecondaryLinks links={resource.secondaryLinks} className="g-mt-8" />
              </ArticleAuxiliary>
            )}

            {resource.citation && (
              <ArticleAuxiliary
                label={<FormattedMessage id="cms.auxiliary.citation" />}
                dangerouslySetValue={{
                  __html: resource.citation,
                  classNames: 'underlineLinks',
                }}
              />
            )}

            {resource.author && (
              <ArticleAuxiliary
                label={<FormattedMessage id="cms.resource.author" />}
                dangerouslySetValue={{ __html: resource.author, classNames: 'underlineLinks' }}
              />
            )}

            {resource.rights && (
              <ArticleAuxiliary
                label={<FormattedMessage id="cms.resource.rights" />}
                dangerouslySetValue={{ __html: resource.rights, classNames: 'underlineLinks' }}
              />
            )}

            {resource.rightsHolder && (
              <ArticleAuxiliary
                label={<FormattedMessage id="cms.resource.rightsHolder" />}
                dangerouslySetValue={{
                  __html: resource.rightsHolder,
                  classNames: 'underlineLinks',
                }}
              />
            )}
          </ArticleFooterWrapper>
        </ArticleTextContainer>
      </PageContainer>
    </article>
  );
}
